import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";

function useBillingInvoices({ currentUserId, userId, t, language }) {
	const [billingInvoices, setBillingInvoices] = useState([]);
	const [billingInvoicesTotal, setBillingInvoicesTotal] = useState(0);
	const [billingInvoicesHasMore, setBillingInvoicesHasMore] = useState(false);
	const [billingInvoicesLoaded, setBillingInvoicesLoaded] = useState(false);
	const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

	useEffect(() => {
		if (!currentUserId || currentUserId !== userId) {
			return;
		}

		let cancelled = false;

		const loadBillingInvoices = async () => {
			try {
				const response = await axiosClient.get("/api/users/my-invoices");
				if (cancelled) {
					return;
				}

				setBillingInvoices(response.data?.data?.items || []);
				setBillingInvoicesTotal(response.data?.data?.total || 0);
				setBillingInvoicesHasMore(Boolean(response.data?.data?.hasMore));
				setBillingInvoicesLoaded(true);
			} catch {
				if (cancelled) {
					return;
				}

				setBillingInvoices([]);
				setBillingInvoicesTotal(0);
				setBillingInvoicesHasMore(false);
				setBillingInvoicesLoaded(true);
			}
		};

		void loadBillingInvoices();

		return () => {
			cancelled = true;
		};
	}, [currentUserId, userId]);

	const handleInvoiceDownload = useCallback(
		async (invoiceId) => {
			const downloadWindow = window.open("", "_blank", "noopener,noreferrer");

			try {
				setLoadingInvoiceId(invoiceId);
				const response = await axiosClient.get(
					`/api/users/my-invoices/${invoiceId}/download-url`,
				);
				const downloadUrl = response.data?.data?.downloadUrl;
				if (!downloadUrl) {
					throw new Error("Missing download URL");
				}

				if (downloadWindow) {
					downloadWindow.location = downloadUrl;
				} else {
					window.location.assign(downloadUrl);
				}
			} catch {
				if (downloadWindow) {
					downloadWindow.close();
				}
				toast.error(t("settings.billing.invoices.downloadError"));
			} finally {
				setLoadingInvoiceId(null);
			}
		},
		[t],
	);

	const getInvoicesSummary = useCallback(() => {
		if (billingInvoicesTotal === 1) {
			return t("settings.billing.invoices.singleSummary");
		}

		return t("settings.billing.invoices.multipleSummary", {
			count: billingInvoicesTotal,
		});
	}, [billingInvoicesTotal, t]);

	const formatInvoiceAmount = useCallback(
		(amount, currency) =>
			new Intl.NumberFormat(language === "es" ? "es-ES" : "en-US", {
				style: "currency",
				currency: (currency || "eur").toUpperCase(),
				minimumFractionDigits: 2,
			}).format((Number(amount) || 0) / 100),
		[language],
	);

	const formatInvoiceDate = useCallback(
		(value) => {
			if (!value) {
				return "";
			}

			return new Intl.DateTimeFormat(language === "es" ? "es-ES" : "en-US", {
				year: "numeric",
				month: "short",
				day: "numeric",
			}).format(new Date(value));
		},
		[language],
	);

	const getInvoiceStatusLabel = useCallback(
		(status) =>
			status === "void"
				? t("settings.billing.invoices.status.void")
				: t("settings.billing.invoices.status.issued"),
		[t],
	);

	return {
		billingInvoices,
		billingInvoicesTotal,
		billingInvoicesHasMore,
		billingInvoicesLoaded,
		loadingInvoiceId,
		handleInvoiceDownload,
		getInvoicesSummary,
		formatInvoiceAmount,
		formatInvoiceDate,
		getInvoiceStatusLabel,
	};
}

export default useBillingInvoices;
